import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Camera, Cable, Volume2, ArrowRight, MessageCircle, Cpu, KeyRound,
  Radio, Server, MapPin, BadgeCheck, Layers,
} from "lucide-react";

const IMG = {
  hero: "https://images.unsplash.com/photo-1496368077930-c1e31b4e5b44?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwyfHxjY3R2JTIwc2VjdXJpdHklMjBjYW1lcmElMjBzdXJ2ZWlsbGFuY2UlMjBidWlsZGluZ3xlbnwwfHx8fDE3ODgzNDEzNDB8MA&ixlib=rb-4.1.0&q=85",
  cctv: "https://images.unsplash.com/photo-1618482914248-29272d021005?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxjY3R2JTIwc2VjdXJpdHklMjBjYW1lcmElMjBzdXJ2ZWlsbGFuY2UlMjBidWlsZGluZ3xlbnwwfHx8fDE3ODgzNDEzNDB8MA&ixlib=rb-4.1.0&q=85",
  elv: "https://images.unsplash.com/photo-1691435828932-911a7801adfb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxzbWFydCUyMGJ1aWxkaW5nJTIwZWx2JTIwc3lzdGVtJTIwc2VydmVyJTIwcm9vbSUyMG5ldHdvcmslMjBjYWJsaW5nfGVufDB8fHx8MTc4ODM0MTM0MHww&ixlib=rb-4.1.0&q=85",
  av: "https://images.unsplash.com/photo-1680992046626-418f7e910589?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxzbWFydCUyMGJ1aWxkaW5nJTIwZWx2JTIwc3lzdGVtJTIwc2VydmVyJTIwcm9vbSUyMG5ldHdvcmslMjBjYWJsaW5nfGVufDB8fHx8MTc4ODM0MTM0MHww&ixlib=rb-4.1.0&q=85",
};

const solutions = [
  {
    icon: Camera,
    title: "Security Systems",
    desc: "4K IP CCTV surveillance, biometric access control, intrusion alarms, gate barriers and turnstiles — engineered for total facility protection.",
    img: IMG.cctv,
    path: "/security-systems",
    chips: ["4K IP CCTV", "AI ANALYTICS", "ACCESS CONTROL", "INTRUSION ALARM"],
    testid: "solution-security-card",
  },
  {
    icon: Cable,
    title: "ELV Systems",
    desc: "Structured cabling, fiber optics, video intercom, SMATV/IPTV and network infrastructure — the backbone of every smart building.",
    img: IMG.elv,
    path: "/elv-systems",
    chips: ["CAT6 / FIBER", "SMATV / IPTV", "INTERCOM", "SERVER RACKS"],
    testid: "solution-elv-card",
  },
  {
    icon: Volume2,
    title: "PA & AV Systems",
    desc: "Public address, background music, conference room AV, video walls and digital signage for corporate and retail environments.",
    img: IMG.av,
    path: "/pa-av-systems",
    chips: ["PA / BGM", "VIDEO WALL", "CONFERENCE AV", "DIGITAL SIGNAGE"],
    testid: "solution-pa-av-card",
  },
];

const highlights = [
  { icon: Cpu, title: "AI-Powered CCTV", desc: "Smart detection, facial recognition and remote monitoring from anywhere.", chips: ["4K UHD", "IP67", "AI ANALYTICS"] },
  { icon: KeyRound, title: "Access Control", desc: "Biometric, RFID and Face ID door access with centralised management.", chips: ["BIOMETRIC", "RFID", "FACE ID"] },
  { icon: Radio, title: "Public Address", desc: "Zone-based PA and BGM for offices, retail, hotels and factories.", chips: ["ZONING", "BGM", "EVAC READY"] },
  { icon: Server, title: "ELV Infrastructure", desc: "Certified structured cabling and fiber backbones engineered for reliability and scale.", chips: ["CAT6A", "FIBER OPTIC", "FLUKE TESTED"] },
];

const stats = [
  { icon: BadgeCheck, value: "2019", label: "Registered Malaysian Company" },
  { icon: MapPin, value: "KL HQ", label: "Menara Keck Seng, Bukit Bintang" },
  { icon: Layers, value: "3 Divisions", label: "Security • ELV • PA/AV" },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <img src={IMG.hero} alt="CCTV security camera" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0F1D]/80 via-[#0A0F1D]/70 to-[#0A0F1D]" />
        <div className="absolute inset-0 grid-overlay" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 w-full">
          <motion.div initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
            <span className="tech-label">Trading of ELV, CCTV &amp; Security Systems — Kuala Lumpur</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mt-4 leading-tight">
              Integrated Security &amp; ELV Solutions for Modern Facilities
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl">
              YC SYSTEM designs, supplies and integrates CCTV surveillance, access control,
              ELV infrastructure and PA/AV systems for offices, factories, residences and
              retail across Malaysia.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/quote"
                data-testid="hero-cta-quote"
                className="px-7 py-3.5 bg-blue-600 hover:bg-blue-500 text-white font-semibold transition-colors border border-cyan-400/40 flex items-center gap-2"
              >
                Request a Quote <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="https://wa.me/601172283321?text=Hello%20YC%20SYSTEM%2C%20I%20would%20like%20to%20enquire%20about%20your%20ELV%2FCCTV%2FSecurity%20Systems."
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-whatsapp"
                className="px-7 py-3.5 bg-transparent hover:bg-[#25D366]/10 text-white font-semibold transition-colors border border-[#25D366]/60 flex items-center gap-2"
              >
                <MessageCircle className="w-4 h-4 text-[#25D366]" /> WhatsApp Us
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/10 border border-white/10"
          >
            {stats.map((s) => (
              <div key={s.label} className="bg-[#0A0F1D]/90 p-5" data-testid={`stat-${s.value.toString().toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <s.icon className="w-5 h-5 text-cyan-400 mb-2" />
                <div className="font-display text-white font-bold text-lg">{s.value}</div>
                <div className="text-xs text-slate-400 mt-1">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* SOLUTIONS */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
            <div>
              <span className="tech-label">Core Solutions</span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-white mt-2">
                One Partner. Complete Low-Voltage Expertise.
              </h2>
            </div>
            <Link to="/quote" className="text-cyan-400 text-sm font-semibold flex items-center gap-1 hover:gap-2 transition-all" data-testid="solutions-quote-link">
              Get a tailored proposal <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {solutions.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.12 }}
              >
                <Link to={s.path} className="glow-card block overflow-hidden group" data-testid={s.testid}>
                  <div className="relative h-52 overflow-hidden">
                    <img src={s.img} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] to-transparent" />
                    <div className="absolute bottom-4 left-4 w-11 h-11 bg-blue-600 flex items-center justify-center border border-cyan-400/40">
                      <s.icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-xl font-semibold text-white">{s.title}</h3>
                    <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.desc}</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {s.chips.map((c) => <span key={c} className="spec-chip">{c}</span>)}
                    </div>
                    <div className="mt-5 text-cyan-400 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                      Explore systems <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HIGHLIGHTS */}
      <section className="py-20 bg-[#0F172A]/60 border-y border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="tech-label">Featured Capabilities</span>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-white mt-2 mb-12">
            Featured Product Highlights
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {highlights.map((h, i) => (
              <motion.div
                key={h.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="glow-card p-6"
                data-testid={`highlight-${h.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <div className="w-11 h-11 bg-blue-600/20 border border-cyan-400/30 flex items-center justify-center mb-4">
                  <h.icon className="w-6 h-6 text-cyan-400" />
                </div>
                <h3 className="font-display text-lg font-semibold text-white">{h.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{h.desc}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {h.chips.map((c) => <span key={c} className="spec-chip">{c}</span>)}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="glow-card p-8 lg:p-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8" data-testid="home-cta-band">
            <div className="max-w-2xl">
              <span className="tech-label">Free Consultation</span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-white mt-2">
                Planning a new facility or upgrading security?
              </h2>
              <p className="mt-4 text-slate-300 leading-relaxed">
                Tell us about your site — our engineers will design a compliant, scalable
                system and send you a detailed quotation.
              </p>
            </div>
            <Link
              to="/quote"
              data-testid="home-cta-quote-button"
              className="shrink-0 px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white font-semibold transition-colors border border-cyan-400/40 flex items-center gap-2"
            >
              Request a Quote <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
